import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft, ShieldCheck, Activity, Cpu, Plane, Radar, FileText } from "lucide-react";
import PageShell from "../components/PageShell";
import { Section, SectionHeading, Reveal } from "../lib/ui";
import { SERVICE_CATEGORIES } from "../config";
import { SERVICE_IMAGES } from "../lib/images";

const ICONS = { "bespoke-security": ShieldCheck, "risk-management": Activity, "technical-solutions": Cpu, "travel-risk-management": Plane, "wider-risk-management": Radar, "strategic-deliverables": FileText };

export default function ServicesIndex() {
  const navigate = useNavigate();
  return (
    <PageShell>
      <section className="px-6 md:px-10 lg:px-16 pt-36 pb-4 md:pt-40 overflow-hidden">
        <div className="max-w-7xl mx-auto relative">
          <div className="absolute -top-24 right-0 h-72 w-72 rounded-full bg-gold/5 blur-3xl pointer-events-none" />
          <button onClick={() => navigate("/")} className="inline-flex items-center gap-2 text-silver/60 hover:text-gold transition-colors text-sm font-body mb-8" data-testid="services-back">
            <ArrowLeft size={16} className="rtl-flip" /> Back to home
          </button>
          <SectionHeading
            overline="What We Do"
            title={<>Our <span className="gold-text">Services</span></>}
            kicker="Choose the area you're interested in — each page focuses only on that service, so you can quickly find exactly what you need."
          />
        </div>
      </section>

      <Section>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICE_CATEGORIES.map((c, i) => {
            const Icon = ICONS[c.slug] || ShieldCheck;
            return (
              <Reveal key={c.slug} delay={(i % 3) * 0.08}>
                <Link
                  to={`/services/${c.slug}`}
                  className="group text-left w-full h-full hairline rounded-2xl bg-ink-surface/60 overflow-hidden hover:border-gold/40 transition-colors duration-500 flex flex-col"
                  data-testid={`service-cat-${c.slug}`}
                >
                  <div className="relative h-36 overflow-hidden">
                    <img src={SERVICE_IMAGES[c.slug]} alt="" className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-700" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink-surface via-ink-surface/30 to-transparent" />
                    <span className="absolute bottom-3 left-4 h-11 w-11 rounded-full border border-gold/40 bg-ink/70 backdrop-blur flex items-center justify-center text-gold">
                      <Icon size={20} strokeWidth={1.4} />
                    </span>
                  </div>
                  <div className="p-7 flex flex-col flex-1">
                    <h3 className="font-display font-light text-2xl text-white group-hover:text-gold-light transition-colors">{c.label}</h3>
                    <p className="mt-3 text-silver/60 font-body font-light text-sm leading-relaxed flex-1">{c.tagline}</p>
                    <span className="mt-6 inline-flex items-center gap-2 text-sm font-body text-gold group-hover:gap-3 transition-all duration-300">
                      Learn more <ArrowUpRight size={15} />
                    </span>
                  </div>
                </Link>
              </Reveal>
            );
          })}
          <Reveal delay={0.16}>
            <Link to="/contact" className="text-left w-full h-full rounded-2xl border border-gold/25 bg-gold/[0.04] p-7 flex flex-col justify-center hover:bg-gold/[0.08] transition-colors" data-testid="services-contact-card">
              <h3 className="font-display font-light text-2xl text-white">Not sure where to start?</h3>
              <p className="mt-3 text-silver/70 font-body font-light text-sm">Get in contact and we'll guide you to the right service for your requirements.</p>
              <span className="mt-6 inline-flex items-center gap-2 text-sm font-body text-gold">Get in contact <ArrowUpRight size={15} /></span>
            </Link>
          </Reveal>
        </div>
      </Section>
    </PageShell>
  );
}
